import React from 'react';
import { getMonthName } from '@/config/gameConstants';
import type { NewsStory } from './useNewspaperEvents';

interface NewspaperSpreadProps {
  leftStory: NewsStory | null;
  rightStory: NewsStory | null;
  pageNumbers: [number, number];
}

const CATEGORY_COLORS: Record<string, string> = {
  economic: 'bg-amber-100 text-amber-800 border-amber-200',
  technology: 'bg-blue-100 text-blue-800 border-blue-200',
  competition: 'bg-red-100 text-red-800 border-red-200',
  regulation: 'bg-purple-100 text-purple-800 border-purple-200',
  social: 'bg-green-100 text-green-800 border-green-200',
  press: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  development: 'bg-orange-100 text-orange-800 border-orange-200',
};

function SpreadCategoryBadge({ category }: { category: string }) {
  const colors = CATEGORY_COLORS[category] ?? 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span
      className={`inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider
        rounded border ${colors}`}
    >
      {category}
    </span>
  );
}

function StoryColumn({ story }: { story: NewsStory | null }) {
  if (!story) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
        <p className="text-xs text-amber-800/40 italic">More stories coming soon</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col px-4 py-3 min-h-0 overflow-hidden">
      <div className="mb-2">
        <SpreadCategoryBadge category={story.category} />
      </div>

      <h4 className="text-base font-display font-bold leading-snug text-amber-950 mb-2">
        {story.headline}
      </h4>

      <p className="text-xs leading-relaxed text-amber-900/75 line-clamp-6 flex-1">
        {story.body}
      </p>

      <div className="mt-2 text-[9px] text-amber-800/45 uppercase tracking-wider">
        {getMonthName(story.gameDate.month)} {story.gameDate.day}, {story.gameDate.year}
      </div>
    </div>
  );
}

export const NewspaperSpread: React.FC<NewspaperSpreadProps> = ({
  leftStory,
  rightStory,
  pageNumbers,
}) => {
  return (
    <div className="flex flex-col h-full">
      {/* Top decorative rule */}
      <div className="border-b border-amber-900/15 mx-6 pt-4" />

      {/* Two-column layout */}
      <div className="flex-1 flex min-h-0">
        {/* Left column */}
        <StoryColumn story={leftStory} />

        {/* Spine / vertical divider */}
        <div className="w-px bg-amber-900/15 my-4" />

        {/* Right column */}
        <StoryColumn story={rightStory} />
      </div>

      {/* Bottom with page numbers */}
      <div className="border-t border-amber-900/15 mx-6 pb-3 pt-2 flex justify-between">
        <span className="text-[8px] text-amber-800/30 tracking-widest uppercase">
          Page {pageNumbers[0]}
        </span>
        <span className="text-[8px] text-amber-800/30 tracking-widest uppercase">
          Page {pageNumbers[1]}
        </span>
      </div>
    </div>
  );
};

export default NewspaperSpread;
